import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Person, PersonClient, PersonCreateModel, Role, RoleClient, Gender } from '../../utils/apiClient';
import { useAuth } from '../Auth/AuthContext';
import ApiUrl from '../../utils/config';
import AppContext from '../../AppContext';
import { handleError } from '../../utils';
import { FormPage, ChipGroup, PersonFields } from '../../common/components/FormKit';

interface PersonForm {
  lastName: string;
  firstName: string;
  patronymic: string;
  birthDate: Date;
  city: string;
  gender?: Gender;
  roleIds: number[];
}

const UpdatePerson = () => {
  const appContext = React.useContext(AppContext);
  const { id } = useParams<{ id: string }>();
  const [person, setPerson] = useState<Person | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();

  const personsClient = new PersonClient(ApiUrl, axios);
  const rolesClient = new RoleClient(ApiUrl, axios);

  const [formData, setFormData] = useState<PersonForm>({
    lastName: '',
    firstName: '',
    patronymic: '',
    birthDate: new Date(),
    city: '',
    gender: undefined,
    roleIds: [],
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const personData = await personsClient.getPerson(parseInt(id!));
        setPerson(personData);

        // Список ролей нужен только администратору: менять роли может только он
        if (isAdmin()) {
          setRoles(await rolesClient.getRoles());
        }

        setFormData({
          lastName: personData.lastName,
          firstName: personData.firstName,
          patronymic: personData.patronymic || '',
          birthDate: personData.birthDate ? new Date(personData.birthDate) : new Date(),
          city: personData.city,
          gender: personData.gender,
          roleIds: personData.personRoles?.map(pr => pr.roleId!) || [],
        });
      } catch (ex) {
        handleError(appContext!.setNotificationData, ex);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id]);

  // Проверка, имеет ли пользователь права на редактирование этой персоны
  useEffect(() => {
    // Ждем, пока загрузятся и пользователь, и персона
    if (!loading && person && user) {
      // Разрешаем только если пользователь редактирует свой профиль или является администратором
      if (user.id !== person.id && !isAdmin()) {
        navigate(`/persons/${id}`);
      }
    }
  }, [loading, person, user, id, navigate, isAdmin]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSuccess(false);
    setSaving(true);

    try {
      await personsClient.updatePerson(parseInt(id!), new PersonCreateModel({
        lastName: formData.lastName,
        firstName: formData.firstName,
        patronymic: formData.patronymic,
        birthDate: formData.birthDate,
        city: formData.city,
        gender: formData.gender,
        // Роли отправляем только от администратора: сервер игнорирует их у остальных
        roleIds: isAdmin() ? formData.roleIds : undefined,
      }));

      setSuccess(true);

      // Перенаправление после короткой задержки
      setTimeout(() => {
        navigate(`/persons/${id}`);
      }, 1500);
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex);
      setSaving(false);
    }
  };

  if (loading) {
    return <div>Загрузка данных...</div>;
  }

  if (!person) {
    return <div>Пользователь не найден</div>;
  }

  return (
    <FormPage title="Редактирование профиля" subtitle={`${person.lastName} ${person.firstName}`}>
      <form className="form-page__form" onSubmit={handleSubmit}>
        {success && <div className="form-page__notice" role="status">Данные успешно сохранены</div>}

        <PersonFields
          idPrefix="edit"
          value={formData}
          onChange={(patch) => setFormData((prev) => ({ ...prev, ...patch }))}
        />

        {isAdmin() && (
          <ChipGroup
            label="Роли"
            type="checkbox"
            name="edit-roles"
            emptyText="Список ролей недоступен"
            options={roles.map((role) => ({ value: role.id!, label: role.name! }))}
            selected={formData.roleIds}
            onToggle={(roleId, checked) => setFormData((prev) => ({
              ...prev,
              roleIds: checked
                ? [...prev.roleIds, Number(roleId)]
                : prev.roleIds.filter((rid) => rid !== Number(roleId)),
            }))}
          />
        )}

        <div className="form-page__actions">
          <button type="button" className="btn btn--back" onClick={() => navigate(`/persons/${id}`)}>Отмена</button>
          <button type="submit" className="btn form-page__submit" disabled={saving}>
            {saving ? 'Сохранение…' : 'Сохранить'}
          </button>
        </div>
      </form>
    </FormPage>
  );
};

export default UpdatePerson;
